<template>
    <div class="max-w-7xl mx-auto space-y-8 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage your company settings, subscriptions, and system configuration
          </p>
        </div>
      </div>

      <!-- Module Settings Section -->
      <div class="space-y-4">
        <div class="border-b border-gray-200 dark:border-gray-700 pb-2">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Module Settings</h2>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Configure settings for each module
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- IT Helpdesk Settings Card -->
          <Link :href="route('settings.tickets.index')">
            <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <div class="p-6">
                <div class="flex items-center space-x-4 mb-4">
                  <div class="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">IT Helpdesk Settings</h3>
                  </div>
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Configure ticket categories, SLA policies, and service desk settings
                </p>
              </div>
            </Card>
          </Link>

          <!-- Maintenance Work Order Settings Card -->
          <Link :href="route('settings.maintenance.index')">
            <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <div class="p-6">
                <div class="flex items-center space-x-4 mb-4">
                  <div class="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Maintenance Work Order Settings</h3>
                  </div>
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Manage service categories, maintenance services, SLA policies, and assets
                </p>
              </div>
            </Card>
          </Link>

          <!-- Incident Management Settings Card -->
          <Link :href="route('settings.incidents.index')">
            <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <div class="p-6">
                <div class="flex items-center space-x-4 mb-4">
                  <div class="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Incident Management Settings</h3>
                  </div>
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Configure incident types, severity levels, escalation rules, and response procedures
                </p>
              </div>
            </Card>
          </Link>

          <!-- Locations Setup Card -->
          <Link :href="route('settings.locations.index')">
            <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <div class="p-6">
                <div class="flex items-center space-x-4 mb-4">
                  <div class="w-12 h-12 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Locations Setup</h3>
                  </div>
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Define and manage locations for company assets and IT equipment
                </p>
              </div>
            </Card>
          </Link>
        </div>
      </div>

      <!-- Company Settings Section -->
      <div class="space-y-4">
        <div class="border-b border-gray-200 dark:border-gray-700 pb-2">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Company Settings</h2>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage your company profile and team
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- Company Information Card -->
          <Link :href="route('settings.company')">
            <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <div class="p-6">
                <div class="flex items-center space-x-4 mb-4">
                  <div class="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Company Information</h3>
                  </div>
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  View and update your company information, address, contact details, and branding
                </p>
              </div>
            </Card>
          </Link>

          <!-- Subscription & Billing Card - Only for Company Subscriber in SaaS mode -->
          <Link v-if="isCompanySubscriber && isSaasMode" :href="route('settings.billing.index')">
            <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <div class="p-6">
                <div class="flex items-center space-x-4 mb-4">
                  <div class="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Subscription & Billing</h3>
                  </div>
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Manage your subscription plan, billing information, payment methods, and invoices
                </p>
              </div>
            </Card>
          </Link>

          <!-- Manage Users Card -->
          <Link :href="route('team.index')">
            <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <div class="p-6">
                <div class="flex items-center space-x-4 mb-4">
                  <div class="w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                    <svg class="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Manage Users</h3>
                  </div>
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Invite and manage team members, assign roles, and control user access
                </p>
              </div>
            </Card>
          </Link>
        </div>
      </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import route from '@/Utils/route';

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
  subscription: {
    type: Object,
    default: null,
  },
  logo_url: {
    type: String,
    default: null,
  },
});

// Check if user has Company Subscriber role
const page = usePage();
const roles = computed(() => page.props.auth?.roles || []);
const isCompanySubscriber = computed(() => roles.value.includes('company_subscriber'));

// Check if app is in SaaS mode (billing features should be hidden in Enterprise mode)
const isSaasMode = computed(() => page.props.deployment?.isSaas ?? false);
</script>
